import { useEffect, useRef, useState } from 'react';
import { loadModules, setDefaultOptions } from 'esri-loader';
import { Event } from '@/hooks/useEvents';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';

interface EventsMapProps {
  events: Event[];
  className?: string;
}

export const EventsMap = ({ events, className = '' }: EventsMapProps) => {
  const mapDiv = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [mapError, setMapError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!mapDiv.current) return;

    setDefaultOptions({ version: "4.33" });

    let view: any = null;

    loadModules([
      "esri/Map",
      "esri/views/MapView",
      "esri/layers/VectorTileLayer",
      "esri/Graphic",
      "esri/geometry/Point",
      "esri/widgets/Zoom",
    ], { css: true })
      .then(([EsriMap, MapView, VectorTileLayer, Graphic, Point, Zoom]) => {
        const outdoorsLayer = new VectorTileLayer({
          portalItem: { id: "ef4bc627ccab4a80990ed476d53020d6" },
        });

        const map = new EsriMap({
          layers: [outdoorsLayer],
        });

        view = new MapView({
          container: mapDiv.current as HTMLDivElement,
          map,
          center: [-118.2437, 34.0522],
          zoom: 9,
          ui: {
            components: []
          }
        });

        const zoomWidget = new Zoom({
          view: view,
        });
        view.ui.add(zoomWidget, "top-right");

        // Filter events with meeting points
        const eventsWithLocations = events.filter(event => event.meeting_point_geom);

        if (eventsWithLocations.length > 0) {
          const graphics: any[] = [];

          eventsWithLocations.forEach(event => {
            if (!event.meeting_point_geom) return;

            const coords = event.meeting_point_geom.coordinates || 
                          (typeof event.meeting_point_geom === 'string' 
                            ? JSON.parse(event.meeting_point_geom).coordinates 
                            : null);

            if (!coords || coords.length !== 2) return;

            const [lng, lat] = coords;

            const point = new Point({
              longitude: lng,
              latitude: lat
            });

            const markerSymbol = {
              type: "simple-marker",
              color: [14, 165, 233],
              size: "16px",
              outline: {
                color: [255, 255, 255],
                width: 2
              }
            };

            const popupTemplate = {
              title: event.title,
              content: `<div class="p-2">
                <p class="text-sm text-muted-foreground">${event.event_type.replace('_', ' ')}</p>
              </div>`
            };

            const graphic = new Graphic({
              geometry: point,
              symbol: markerSymbol,
              attributes: {
                eventId: event.id,
                title: event.title,
                eventType: event.event_type
              },
              popupTemplate: popupTemplate
            });

            graphics.push(graphic);
            view.graphics.add(graphic);
          });

          // Fit map to markers
          if (graphics.length > 0) {
            const allPoints = graphics.map(g => g.geometry);
            view.goTo(allPoints, { padding: 50, maxZoom: 12 });
          }

          // Add click handler to navigate to event detail
          view.on("click", (event: any) => {
            view.hitTest(event).then((response: any) => {
              if (response.results.length > 0) {
                const graphic = response.results[0].graphic;
                if (graphic.attributes?.eventId) {
                  navigate(`/events/${graphic.attributes.eventId}`);
                }
              }
            });
          });
        }

        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error initializing map:', error);
        setMapError('Failed to initialize map');
        setIsLoading(false);
      });

    return () => {
      if (view) {
        view.container = null;
        view.destroy();
        view = null;
      }
    };
  }, [events, navigate]);

  if (isLoading) {
    return <Skeleton className={`w-full ${className}`} />;
  }

  if (mapError) {
    return (
      <div className={`w-full ${className} flex items-center justify-center bg-muted rounded-lg`}>
        <p className="text-muted-foreground">{mapError}</p>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div ref={mapDiv} className="absolute inset-0 rounded-lg" />
      {events.filter(e => e.meeting_point_geom).length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 rounded-lg">
          <p className="text-muted-foreground">No events with locations to display</p>
        </div>
      )}
    </div>
  );
};
